import { useCallback, useState, useEffect } from 'react';
import { getObjectValues, getObjectEntries, containsKeys } from '@utils/typescriptGuards';
import { FilterObject, FilterValue } from 'components/filtering/types/FilterObject';
import { createFilterObject } from 'components/filtering/filter-logic/createFilterObject';



export const useGridFiltering = <T extends object>(originData: Array<T> | undefined | null) => {

	const [ filterObject, setFilterObject ] = useState<FilterObject<T> | null>(null);
	const [ filteredData, setFilteredData ] = useState<Array<T> | null>(null);


	useEffect(() => {
		if (!originData) {
			setFilteredData(null);
			setFilterObject(null);
			return;
		}
		if (originData && originData.length === 0) {
			setFilteredData([]);
			setFilterObject(null);
			return;
		}
		let filteredObject = createFilterObject(originData);
		setFilterObject(filteredObject);
	}, [ originData ]);


	useEffect(() => {
		if (filterObject && originData && originData.length !== 0) {
			let hidesRowIndexes: Set<number> = new Set();

			for (const filterValues of getObjectValues(filterObject)) {
				
				for (const filterValue of filterValues) {
					
					if (filterValue.checked === false) {
						hidesRowIndexes.add(filterValue.rowIndex);
					}
				}
			}

			let filteredData = originData.filter((_, dataItemIndex) => hidesRowIndexes.has(dataItemIndex) ? false : true);
			setFilteredData(filteredData);
		}
	}, [ filterObject, originData ]);



	const handleAddFilterValues = useCallback((property: keyof T, showedFilterValues: FilterValue[]) => {
		if (!filterObject || !showedFilterValues || showedFilterValues.length === 0) return;

		let filterValues = filterObject[property];
		if (!filterValues || filterValues.length === 0) return;
		
		// prepare set of checked and unchecked values
		let checkedValuesSet: Set<unknown> = new Set();
		let uncheckedValuesSet: Set<unknown> = new Set();

		for (const showedFilterValue of showedFilterValues) {
			showedFilterValue.checked === true
			? checkedValuesSet.add(showedFilterValue.value)
			: uncheckedValuesSet.add(showedFilterValue.value);
		}

		// get affected filter vales id and row index
		let checkedRowIndexes: Set<number> = new Set();
		let checkedFilterValueIds: Set<string> = new Set();
		let uncheckedRowIndexes: Set<number> = new Set();
		let uncheckedFilterValueIds: Set<string> = new Set();

		for (const filterValue of filterValues) {
			if (checkedValuesSet.has(filterValue.value)) {
				checkedRowIndexes.add(filterValue.rowIndex);
				checkedFilterValueIds.add(filterValue.id);
				continue;
			}
			if (uncheckedValuesSet.has(filterValue.value)) {
				uncheckedRowIndexes.add(filterValue.rowIndex);
				uncheckedFilterValueIds.add(filterValue.id);
				continue;
			}
		}

		// check, if same values as checked values is not hidden by another column
/* 		let checkedRowIndexesArrayEntries = Array.from(checkedRowIndexes);
		let checkedFilterValueIdsArray = Array.from(checkedFilterValueIds);

		for (const filterValues of getObjectValues(filterObject)) {

			checkedRowIndexesArrayEntries.forEach((checkedRowIndexesValue, index) => {
				
				const checkedRowIdValue = filterValues[checkedRowIndexesValue];
			
				if (checkedRowIdValue && checkedRowIdValue.checked === false) {
					let checkedFilterValueIdsValue = checkedFilterValueIdsArray[index];

					if (!checkedFilterValueIdsValue) return;

					checkedRowIndexes.delete(checkedRowIndexesValue);
					checkedFilterValueIds.delete(checkedFilterValueIdsValue);
					uncheckedRowIndexes.add(checkedRowIndexesValue);
					uncheckedFilterValueIds.add(checkedFilterValueIdsValue);
				}

			});
		} */

		let newFilterObject: Partial<FilterObject<T>> = {};
		for (const [ filterObjectKey, filterValues ] of getObjectEntries(filterObject)) {

			let newFilterValues = filterValues.map(filterValue => {
				
				// checked
				if (checkedFilterValueIds.has(filterValue.id)) return ({
					...filterValue,
					checked: true,
					hidden: false,
				});
				if (checkedRowIndexes.has(filterValue.rowIndex)) return ({
					...filterValue,
					hidden: false,
				});
				
				// unchecked
				if (uncheckedFilterValueIds.has(filterValue.id)) return ({
					...filterValue,
					checked: false,
					hidden: false,
				});
				if (uncheckedRowIndexes.has(filterValue.rowIndex)) return ({
					...filterValue,
					hidden: true,
				});

				return filterValue;
			});

			newFilterObject[filterObjectKey] = newFilterValues;
		}


		if (containsKeys(newFilterObject, Object.keys(filterObject))) {
			setFilterObject(newFilterObject);
		}
	},[ filterObject ]);



	const handleSearchValues = useCallback((property: keyof T, searchValues: unknown[]) => {
		if (!filterObject || !searchValues) return;

		let filterValues = filterObject[property];
		if (!filterValues || filterValues.length === 0) return;
		
		let searchValuesSet: Set<unknown> = new Set(searchValues);
		let searchFilterValuesId: Set<string> = new Set();
		let searchFilterValuesRowIndex: Set<number> = new Set();
		let searchFilterValuesColumnIndex: number = filterValues[0] ? filterValues[0].columnIndex : 0;

		for (const filterValue of filterValues) {
			if (searchValuesSet.has(filterValue.value)) {
				searchFilterValuesId.add(filterValue.id);
				searchFilterValuesRowIndex.add(filterValue.rowIndex);
			}
		}

		let newFilterObject: Partial<FilterObject<T>> = {};
		for (const [ filterObjectKey, filterValues ] of getObjectEntries(filterObject)) {
			
			let newFilterValues = filterValues.map(filterValue => {
				if (searchFilterValuesId.has(filterValue.id)) return ({
					...filterValue,
					checked: true,
					hidden: false,
				});
				if (searchFilterValuesRowIndex.has(filterValue.rowIndex)) return ({
					...filterValue,
					hidden: false,
				});
				if (searchFilterValuesColumnIndex === filterValue.columnIndex) return ({
					...filterValue,
					checked: false,
					hidden: false,
				});
				return ({
					...filterValue,
					hidden: true,
				});
			});

			newFilterObject[filterObjectKey] = newFilterValues;
		}


		if (containsKeys(newFilterObject, Object.keys(filterObject))) {
			setFilterObject(newFilterObject);
		}
	},[ filterObject ]);



	const clearFilter = useCallback((property: keyof T) => {
		if (!filterObject) return;

		let filterValues = filterObject[property];
		if (!filterValues || filterValues.length === 0) return;

		let clearedRowIndexes: Set<number> = new Set();
		let clearedFilterValueIds: Set<string> = new Set();
		
		for (const filterValue of filterValues) {
			if (filterValue.checked === false) {
				clearedRowIndexes.add(filterValue.rowIndex);
				clearedFilterValueIds.add(filterValue.id);
			}
		}
		if (clearedRowIndexes.size === 0 || clearedFilterValueIds.size === 0) return;


		let newFilterObject: Partial<FilterObject<T>> = {};
		for (const [ filterObjectKey, filterValues ] of getObjectEntries(filterObject)) {
			
			let newFilterValues = filterValues.map(filterValue => {
				if (clearedFilterValueIds.has(filterValue.id)) return ({
					...filterValue,
					checked: true,
					hidden: false,
				});
				if (clearedRowIndexes.has(filterValue.rowIndex)) return ({
					...filterValue,
					hidden: false,
				});
				return filterValue;
			});

			newFilterObject[filterObjectKey] = newFilterValues;
		}


		if (containsKeys(newFilterObject, Object.keys(filterObject))) {
			setFilterObject(newFilterObject);
		}
	},[ filterObject ]);


	
	return [ filteredData, filterObject, handleSearchValues, handleAddFilterValues, clearFilter ] as const;
};